// services/api.js
// MODIFICADO: El interceptor de errores ahora ignora las peticiones de 'blob'
// para permitir un manejo de errores personalizado en las funciones de descarga.

import axios from 'axios';
import { message } from 'antd';

// La API se sirve detrás del mismo origen vía el proxy-server (rewrite /api → backend).
// En dev (CRA), el proxy de package.json hace el mismo rewrite a localhost:5010.
// Para apuntar a otro backend, definir REACT_APP_API_URL en el .env del frontend.
const getApiUrl = () => process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000,
  withCredentials: true, // Importante para las cookies
});

// =================================================================
// --- INTERCEPTORES DE AUTENTICACIÓN Y MANEJO DE ERRORES GLOBALES ---
// =================================================================
let isRefreshing = false;
let failedQueue = [];

// Decodifica el payload del JWT (incluso expirado) para leer el companyKey.
// El backend lo necesita en /auth/refresh-token y /auth/logout para identificar
// la cookie correcta cuando hay sesiones simultáneas en distintas pestañas.
const getCompanyKeyFromToken = () => {
  const token = sessionStorage.getItem('accessToken');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.companyKey || null;
  } catch {
    return null;
  }
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('accessToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// frontend/src/services/api.js
// Modificar el interceptor de respuesta con logs detallados

api.interceptors.response.use(
  (response) => {
    console.log('[Interceptor] Respuesta exitosa:', response.config.url);
    return response;
  },
  async (error) => {
    console.log('[Interceptor] ========== ERROR INTERCEPTADO ==========');
    console.log('[Interceptor] URL:', error.config?.url);
    console.log('[Interceptor] Status:', error.response?.status);
    console.log('[Interceptor] Mensaje:', error.response?.data?.message);
    console.log('[Interceptor] Error completo:', error);
    
    const originalRequest = error.config;
    
    // Si es un 401 y no es retry
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      const url = originalRequest.url;
      console.log('[Interceptor] Error 401 detectado en:', url);
      
      // No intentar refresh si ya estamos en login o refresh
      if (url.includes('/auth/login') || url.includes('/auth/refresh-token')) {
          console.log('[Interceptor] Evitando loop - ya estamos en auth');
          return Promise.reject(error);
      }
      
      // Si ya estamos refrescando, agregar a la cola
      if (isRefreshing) {
        console.log('[Interceptor] Ya hay un refresh en proceso, agregando a cola');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return axios(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      console.log('[Interceptor] Intentando refresh token...');
      
      try {
        const companyKey = getCompanyKeyFromToken();
        const { data } = await api.post('/auth/refresh-token', { companyKey });
        console.log('[Interceptor] Refresh exitoso, nuevo token recibido');
        
        const newAccessToken = data.accessToken;
        sessionStorage.setItem('accessToken', newAccessToken);
        api.defaults.headers.common['Authorization'] = 'Bearer ' + newAccessToken;
        originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
        
        processQueue(null, newAccessToken);
        isRefreshing = false;
        
        console.log('[Interceptor] Reintentando petición original');
        return api(originalRequest);
        
      } catch (refreshError) {
        console.log('[Interceptor] ========== REFRESH FALLÓ ==========');
        console.log('[Interceptor] Error:', refreshError.response?.data);
        console.log('[Interceptor] Status:', refreshError.response?.status);
        
        processQueue(refreshError, null);
        isRefreshing = false;
        
        // Verificar si es por inactividad
        if (refreshError.response?.data?.message === 'Sesión expirada por inactividad.') {
            console.log('[Interceptor] Sesión expirada por inactividad detectada');
            sessionStorage.setItem('logoutReason', 'inactivity');
        }      

        console.log('[Interceptor] Limpiando sesión y redirigiendo a login');
        sessionStorage.removeItem('accessToken');
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      }
    }
    
    // Para otros errores, solo rechazar
    console.log('[Interceptor] Error no manejado, rechazando');
    return Promise.reject(error);
  }
);



const autoPrintPdf = (blob, title = 'Documento') => {
    try {
        const url = window.URL.createObjectURL(blob);
        const printWindow = window.open('', '_blank');

        if (!printWindow || printWindow.closed || typeof printWindow.closed === 'undefined') {
            message.error('No se pudo abrir la ventana de impresión. Por favor, deshabilite el bloqueador de ventanas emergentes para este sitio.');
            window.open(url, '_blank');
            return;
        }

        printWindow.document.write(`
            <html>
                <head><title>${title}</title><style>body, html { margin: 0; padding: 0; height: 100%; overflow: hidden; } iframe { border: none; width: 100%; height: 100%; }</style></head>
                <body>
                    <iframe src="${url}"></iframe>
                    <script>
                        const iframe = document.querySelector('iframe');
                        iframe.onload = function() {
                            setTimeout(function() { iframe.contentWindow.print(); }, 500);
                        };
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    } catch (error) {
        console.error("Error en la función de auto-impresión:", error);
        message.error("Ocurrió un error al intentar preparar el documento para impresión.");
    }
};


// =================================================================
// --- DEFINICIÓN DE SERVICIOS POR MÓDULO ---
// =================================================================

const authService = {
    login: async (username, password, companyKey) => {
        const response = await api.post('/auth/login', { username, password, companyKey });
        if (response.data.accessToken) {
            sessionStorage.setItem('accessToken', response.data.accessToken);
        }
        return response.data;
    },
    getMe: () => api.get('/auth/me'),
    logout: async () => {
        try {
            // Mandar companyKey para que el backend borre solo la cookie de esa
            // empresa (sin tirar otras pestañas con sesión activa de otra empresa).
            const companyKey = getCompanyKeyFromToken();
            await api.post('/auth/logout', { companyKey });
        } catch (error) {
            console.error("Fallo al notificar al backend sobre el logout:", error);
        }
        sessionStorage.removeItem('accessToken');
    },
    
};



const adminService = {
    getUsers: () => api.get('/admin/users'),
    createUser: (userData) => api.post('/admin/users', userData),
    updateUser: (id, userData) => api.put(`/admin/users/${id}`, userData),
    deleteUser: (id) => api.delete(`/admin/users/${id}`),
    adminChangePassword: (userId, passwordData) => api.put(`/admin/users/${userId}/change-password`, passwordData),
    changePassword: (passwordData) => api.put('/admin/user/password', passwordData),
    getRoles: () => api.get('/admin/roles'),
    createRole: (roleData) => api.post('/admin/roles', roleData),
    updateRole: (id, roleData) => api.put(`/admin/roles/${id}`, roleData),
    deleteRole: (id) => api.delete(`/admin/roles/${id}`),
    getProfiles: () => api.get('/admin/profiles'),
    createProfile: (profileData) => api.post('/admin/profiles', profileData),
    updateProfile: (id, profileData) => api.put(`/admin/profiles/${id}`, profileData),
    deleteProfile: (id) => api.delete(`/admin/profiles/${id}`),
};

const deliveryService = {
  getDeliveries: (filters = {}) => api.get('/deliveries', { params: filters }),
  getDeliveryDetails: (docEntry, formatoQuery = '') => api.get(`/deliveries/${docEntry}${formatoQuery}`),
  getPackingListById: (id) => api.get(`/packing-list/by-id/${id}`),
  savePackingList: (docEntry, packingListData) => api.post(`/deliveries/${docEntry}/packing-list`, packingListData),
  savePfsPackingList: (docEntry, packingListData) => api.post(`/deliveries/${docEntry}/pfs-packing-list`, packingListData),
  cancelPhotoSession: (sessionId) => api.post(`/sessions/${sessionId}/cancel`),
  getItemsDetails: (itemCodes) => api.post('/items-details', { itemCodes }),
  
  generatePDF: async (packingListId) => {
      const path = `/packing-list/${packingListId}/pdf`;
      console.log(`Solicitando PDF a la nueva ruta específica: ${path}`);
      try {
          const response = await api.get(path, { responseType: 'blob' });
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
          return url;
      } catch (error) {
          if (error.response && error.response.data instanceof Blob && error.response.data.type === 'application/json') {
              const errorText = await error.response.data.text();
              const errorJson = JSON.parse(errorText);
              throw new Error(errorJson.message || 'Error desconocido al generar el PDF.');
          }
          throw new Error(error.response?.data?.message || error.message || 'Error de red al generar el PDF.');
      }
  },

  generatePfsReportByPalletPDF: async (packingListId) => {
    const path = `/packing-list/${packingListId}/report-by-pallet`;
    console.log(`Solicitando Reporte por Tarima a la nueva ruta específica: ${path}`);
    try {
        const response = await api.get(path, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        autoPrintPdf(blob, `Reporte_por_Tarima_${packingListId}`);
    } catch (error) {
        if (error.response && error.response.data instanceof Blob && error.response.data.type === 'application/json') {
            const errorText = await error.response.data.text();
            const errorJson = JSON.parse(errorText);
            throw new Error(errorJson.message || 'Error desconocido al generar el reporte.');
        }
        throw new Error(error.response?.data?.message || error.message || 'Error de red al generar el reporte.');
    }
  },

  generateLabels: async (packingListId) => {
    const path = `/packing-list/${packingListId}/labels`;
    console.log(`Solicitando Etiquetas a la nueva ruta específica: ${path}`);
    try {
        const response = await api.get(path, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        autoPrintPdf(blob, `Etiquetas_${packingListId}`);
    } catch (error) {
        if (error.response && error.response.data instanceof Blob && error.response.data.type === 'application/json') {
            const errorText = await error.response.data.text();
            const errorJson = JSON.parse(errorText);
            throw new Error(errorJson.message || 'Error desconocido al generar las etiquetas.');
        }
        throw new Error(error.response?.data?.message || error.message || 'Error de red al generar las etiquetas.');
    }
  },

  deletePackingList: (docEntry, motivo) => api.delete(`/deliveries/${docEntry}/packing-list`, { data: { motivo } }),
  requestDeletion: (docEntry, motivo) => api.post(`/deliveries/${docEntry}/solicitar-eliminacion`, { motivo }),
  approveDeletionRequest: (docEntry) => api.post(`/deliveries/${docEntry}/aprobar-eliminacion`),
  rejectDeletionRequest: (docEntry) => api.post(`/deliveries/${docEntry}/reject-eliminacion`),
  getDeletionRequests: () => api.get('/solicitudes-eliminacion'),
  getDeletionLogs: (params) => api.get('/logs-eliminacion', { params }),
  getPendingDeletionRequestsCount: () => api.get('/solicitudes-eliminacion/count'),
  getEliminatedLists: () => api.get('/eliminados'),
  restorePackingList: (docEntry) => api.post(`/deliveries/${docEntry}/restore`),
  permanentlyDeletePackingList: (docEntry, packingListId) => api.delete(`/deliveries/${docEntry}/permanent-delete`, { data: { packingListId } }),
  createPhotoSession: (docEntry, formatoListado) => api.post(`/deliveries/${docEntry}/photos/session`, { formato: formatoListado }),
  getSessionStatus: (sessionId) => api.get(`/sessions/${sessionId}/status`),
  getSessionMetadata: (sessionId) => api.get(`/sessions/${sessionId}/metadata`),
  addPhotoToSession: (sessionId, photoData, onUploadProgress) => api.post(`/sessions/${sessionId}/photos`, photoData, { onUploadProgress }),
  closePhotoSession: (sessionId) => api.put(`/sessions/${sessionId}/close`),
};

const clientService = {
  getClients: () => api.get('/clients'),
  searchClients: (searchTerm) => api.get('/clients/search', { params: { term: searchTerm } }),
};

const catalogService = {
  getPalletDefinitions: () => api.get('/catalog/pallets'),
  getAllPalletDefinitionsForAdmin: () => api.get('/catalog/pallets/all'),
  createPalletDefinition: (palletData) => api.post('/catalog/pallets', palletData),
  updatePalletDefinition: (id, palletData) => api.put(`/catalog/pallets/${id}`, palletData),
  deletePalletDefinition: (id) => api.delete(`/catalog/pallets/${id}`),
  getClientCodes: () => api.get('/catalog/client-codes'),
  createClientCode: (codeData) => api.post('/catalog/client-codes', codeData),
  updateClientCode: (id, codeData) => api.put(`/catalog/client-codes/${id}`, codeData),
  deleteClientCode: (id) => api.delete(`/catalog/client-codes/${id}`),
  uploadClientCodes: (formData) => api.post('/catalog/client-codes/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getDeletionReasons: () => api.get('/catalog/deletion-reasons'),
  getAllDeletionReasonsForAdmin: () => api.get('/catalog/deletion-reasons/all'),
  createDeletionReason: (reasonData) => api.post('/catalog/deletion-reasons', reasonData),
  updateDeletionReason: (id, reasonData) => api.put(`/catalog/deletion-reasons/${id}`, reasonData),
  deleteDeletionReason: (id) => api.delete(`/catalog/deletion-reasons/${id}`),
};

const settingsService = {
    getSetting: (key) => api.get(`/settings/${key}`),
    updateSetting: (key, value) => api.put(`/settings/${key}`, { value }),
};

const dashboardService = {
    getSummary: () => api.get('/dashboard/summary'),
};

const searchService = {
    globalSearch: (q) => api.get('/search', { params: { q } }),
};

// Bonificación por Pronto Pago (solo lectura, empresa Tecno)
const prontoPagoService = {
    getDocumentos: (filters = {}) => api.get('/pronto-pago/documentos', { params: filters }),
    getResumen: () => api.get('/pronto-pago/resumen'),
};

// Exportar todos los servicios para ser usados en la aplicación
export { authService, adminService, deliveryService, clientService, catalogService, settingsService, dashboardService, searchService, prontoPagoService };
